import { recordEvent } from "./api";

const SESSION_ID_KEY = "sunny_sid";
const VISITOR_ID_COOKIE = "sunny_vid";
const VISITOR_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const UTM_INIT_KEY = "sunny_utm_init";
const UTM_KEY = "sunny_utm";
const UTM_SENT_KEY = "sunny_utm_sent";

function createId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function getSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = createId();
      window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function getVisitorId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const existing = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${VISITOR_ID_COOKIE}=`))
      ?.split("=")[1];
    if (existing) return decodeURIComponent(existing);
    const id = createId();
    document.cookie = `${VISITOR_ID_COOKIE}=${encodeURIComponent(id)}; Max-Age=${VISITOR_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
    return id;
  } catch {
    return undefined;
  }
}

/** Captures UTM parameters from the landing URL once per session. */
function captureUtmParams() {
  try {
    if (window.sessionStorage.getItem(UTM_INIT_KEY)) return;
    window.sessionStorage.setItem(UTM_INIT_KEY, "1");
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    if (source) utm.utmSource = source;
    if (medium) utm.utmMedium = medium;
    if (campaign) utm.utmCampaign = campaign;
    if (Object.keys(utm).length > 0) {
      window.sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch {
    // Storage can be unavailable (private mode); tracking continues without UTM.
  }
}

/** Returns the session's stored UTM values exactly once, for the first page_view. */
function consumeSessionUtm(): Record<string, string> | undefined {
  try {
    if (window.sessionStorage.getItem(UTM_SENT_KEY)) return undefined;
    const raw = window.sessionStorage.getItem(UTM_KEY);
    if (!raw) return undefined;
    window.sessionStorage.setItem(UTM_SENT_KEY, "1");
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return undefined;
  }
}

export function trackEvent(eventType: string, metadata?: Record<string, unknown>) {
  if (typeof window === "undefined") {
    recordEvent(eventType, metadata).catch(() => {
      // Silently ignore analytics failures.
    });
    return;
  }
  captureUtmParams();
  const enrichedMetadata = {
    ...metadata,
    path: window.location.pathname,
    referrerHost: referrerHost(document.referrer),
    sessionId: getSessionId(),
    visitorId: getVisitorId(),
    ...(eventType === "page_view" ? consumeSessionUtm() : undefined),
  };
  // Fire-and-forget; analytics must never block the UI.
  recordEvent(eventType, enrichedMetadata).catch(() => {
    // Silently ignore analytics failures.
  });
}

/** Sends time-on-page via sendBeacon so it survives tab close; falls back to keepalive fetch. */
export function sendPageDuration(durationMs: number) {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({
    eventType: "page_duration",
    metadata: {
      path: window.location.pathname,
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      durationMs,
    },
  });
  try {
    const blob = new Blob([payload], { type: "application/json" });
    if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon("/api/events", blob)) {
      return;
    }
  } catch {
    // Fall through to the keepalive fetch below.
  }
  fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silently ignore analytics failures.
  });
}

function referrerHost(referrer: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    return url.host === window.location.host ? undefined : url.host;
  } catch {
    return undefined;
  }
}
